// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect } from 'react';
import { useLocalMessage } from '@Helpers/';
import { AlertOutput, CustomStatusMapType, AlertStatusStage } from 'thehive-sdk';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/Alert/extractData';
import { AlertEventList } from '@Organisms/Alert/eventList';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { useHistory } from 'react-router';
import RouteBuilder from '@Services/RouteBuilder';
import { ICONS } from '@Constants/iconsApp';
import { ScopeContext } from '@Context/ScopeProvider';
import { useListAllUsers } from '@Queries/userQueries';

import './AlertLayoutSider.scss';

interface AlertLayoutSiderProps {
    eventList: AlertEventList;
    data?: AlertOutput;
    hasPermissions?: boolean;
}

const AlertLayoutSider = ({ data, eventList, hasPermissions = false }: AlertLayoutSiderProps): ReactElement => {
    const i = useLocalMessage();
    const { _id: alertId = '', _createdAt, _createdBy, extraData, caseId, status, stage, assignee } = data || {};
    const { importDate, caseNumber } = extraData || {};
    const history = useHistory();
    const [alertStatus, setAlertStatus] = React.useState<CustomStatusMapType>({});

    const {
        customStatus: {
            alert: { list: alertStatusList },
        },
    } = useContext(ScopeContext);

    const { data: assignableUsers = [] } = useListAllUsers();

    useEffect(() => {
        if (alertStatusList.length) {
            const alertStatusMap = alertStatusList.reduce((acc, curr) => {
                if (curr.stage !== stage) {
                    return acc;
                }

                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setAlertStatus(alertStatusMap);
        }
    }, [alertStatusList, stage]);

    return (
        <div className="alert-layout-sider">
            <div className="alert-layout-sider__header">
                <DisplayMetadata
                    direction="vertical"
                    className="alert-layout-sider__metadata"
                    items={[
                        {
                            icon: <ICONS.SettingIcon />,
                            label: 'id',
                            value: <CopyValue value={alertId}>{alertId}</CopyValue>,
                        },
                        {
                            icon: <ICONS.UserIcon />,
                            label: i('common.created-by'),
                            value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                        },
                        {
                            icon: <ICONS.CreationDateIcon />,
                            label: i('common.created-at'),
                            value: <FormatDate date={_createdAt} />,
                        },
                        {
                            icon: <ICONS.DateIcon />,
                            label: i('common.import-date'),
                            value: <FormatDate date={importDate} />,
                            hidden: !importDate,
                        },
                    ]}
                />
            </div>
            {data && (
                <Details
                    data={{
                        ...data,
                        '#computeSecurity': { tlp: data.tlp, pap: data.pap, severity: data.severity },
                        '#computeCount': {
                            observableCount: data.observableCount,
                            linksCount: data.extraData?.similarCases?.length || 0,
                        },
                        '#computeStatus': {
                            status,
                            statusList: alertStatus,
                            isImported: stage === AlertStatusStage.IMPORTED,
                        },
                        '#computeAssignee': {
                            assignableUsers,
                            assignee,
                        },
                        '#computeCase': caseNumber
                            ? {
                                  caseNumber,
                                  onClick: () => history.push(RouteBuilder.compute('cases.details', { caseId })),
                              }
                            : undefined,
                        importDate,
                    }}
                    extract={extractData({ locale: i, eventList, data, hasPermissions })}
                    className="alert-details"
                />
            )}
        </div>
    );
};

export default AlertLayoutSider;
